import { SubHeader } from "../../Common/layout/SubHeader";
import { Redirect } from "react-router-dom";
import TroveNextPendingKycComponent from "../../PendingKyc/pages/TroveNextPendingKyc";
import { useGetTroveNextProfilesQuery } from "../../../../redux/apiSlice";

// The profiles endpoint now returns an exact `total` alongside `data`, so
// tab counts are a real number, not an approximation - fetched with
// limit:1 (we only need the count, not the rows) to keep it cheap. This
// still adds one background request per counted tab whenever this screen
// loads.
const extractTotalCount = (value) => {
    const candidates = [value?.total, value?.data?.total];
    for (const candidate of candidates) {
        if (typeof candidate === "number") return candidate;
    }
    return null;
};

const useTroveNextTotalCount = (userGroup, status, documentStatus, noDocument) => {
    const { data, isFetching } = useGetTroveNextProfilesQuery({
        query: { status, documentStatus, userGroup, limit: 1, ...(noDocument ? { noDocument: "true" } : {}) },
    });
    const total = extractTotalCount(data);
    if (isFetching && total == null) return null;
    return total != null ? `${total}` : null;
};

const TROVE_NEXT_STATUS_TABS = [
    { key: "pending", label: "Pending", status: "PENDING", documentStatus: "PENDING" },
    { key: "active", label: "Active", status: "ACTIVE" },
    { key: "pending-transition-admin", label: "Transition Minor", status: "PENDING_TRANSITION_ADMIN" },
    { key: "transitioned", label: "Transitioned", status: "TRANSITIONED" },
    // Rejected is identified purely by documentStatus=REJECTED - no status
    // param is sent for this tab at all (see omitStatusInQuery below).
    // `status: "PENDING"` here is still passed through as the statusFilter
    // prop for the table's own UI gating (date filter, confirm-user button) -
    // rejected profiles are still profile-status PENDING upstream - it is
    // just excluded from the outgoing query for this tab. Placed last in the
    // tab order per request.
    { key: "rejected", label: "Rejected", status: "PENDING", documentStatus: "REJECTED", omitStatusInQuery: true },
    // Far right. Pending profiles that haven't uploaded a document yet (documentStatus
    // null / documentsUploaded false). Upstream can't filter on that, so the
    // oversight backend does it when it sees noDocument=true.
    { key: "no-document", label: "No Document", status: "PENDING", noDocument: true },
];

const LEGACY_TROVE_NEXT_TAB_REDIRECTS = {};

export const TroveNextManagement = ({
    match: {
        params: { userGroup, statusTab },
    },
}) => {
    const redirectedStatusTab = LEGACY_TROVE_NEXT_TAB_REDIRECTS[statusTab];
    const activeStatusTab = TROVE_NEXT_STATUS_TABS.find(({ key }) => key === statusTab);

    // Hooks must run unconditionally on every render (Rules of Hooks), so these
    // are called before any of the early-return redirects below - even though
    // their result is only used once we reach the tab UI further down.
    // Only Pending/Active/Transitioned get a count label, per what was asked for -
    // Rejected and Transition Minor are left unlabeled for now.
    const pendingCount = useTroveNextTotalCount(userGroup, "PENDING", "PENDING");
    const noDocumentCount = useTroveNextTotalCount(userGroup, "PENDING", undefined, true);
    const activeCount = useTroveNextTotalCount(userGroup, "ACTIVE");
    const transitionedCount = useTroveNextTotalCount(userGroup, "TRANSITIONED");
    const countsByTabKey = {
        pending: pendingCount,
        "no-document": noDocumentCount,
        active: activeCount,
        transitioned: transitionedCount,
    };

    if (!statusTab) {
        return <Redirect to={`/trove-next/${userGroup}/pending`} />;
    }

    if (redirectedStatusTab) {
        return <Redirect to={`/trove-next/${userGroup}/${redirectedStatusTab}`} />;
    }

    if (!activeStatusTab) {
        return <Redirect to={`/trove-next/${userGroup}/pending`} />;
    }

    return (
        <>
            <SubHeader>
                <SubHeader.Nav>
                    {TROVE_NEXT_STATUS_TABS.map(({ key, label }) => (
                        <SubHeader.NavItem
                            key={key}
                            label={label}
                            to={`/trove-next/${userGroup}/${key}`}
                        />
                    ))}
                </SubHeader.Nav>
            </SubHeader>
            <TroveNextPendingKycComponent
                userGroup={userGroup}
                statusFilter={activeStatusTab.status}
                documentStatusOverride={activeStatusTab.documentStatus}
                omitStatusInQuery={!!activeStatusTab.omitStatusInQuery}
                noDocument={!!activeStatusTab.noDocument}
                approxTotalCount={countsByTabKey[activeStatusTab.key] ?? null}
            />
        </>
    );
};
